import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { UserInviteModal } from "../../../components/modals/UserInviteModal";
import { MDBBtn, MDBIcon } from "mdb-react-ui-kit";
import API from "../../../service/api";
import { DataTable, DataTableStringComparison } from "../../../components/DataTable";
import { AdminPage } from "../AdminPage";
import { SplitScreenDiv } from "../../../components/SplitScreenDiv";
import { UserEdit } from "./UserEdit";

export const UsersPage = ({userData, canAddUser, canEditPermissions, canDeleteUser, onUserUpdate}) => {
    const [users, setUsers] = useState<any[]>([]);
    const [editUserId, setEditUserId] = useState<number|null>(null);
    const [deleteUserId, setDeleteUserId] = useState<number|null>(null);
    const [inviteUser, setInviteUser] = useState(false);
    const { t } = useTranslation();

    const Table = useMemo(() => (
        <DataTable
            data={users}
            height={500}
            onRowSelected={u => setEditUserId(u.id)}
            rowClassname={u => u.pending ? 'bg-warning bg-opacity-25 fst-italic' : ''}
            columns={[
                { headerName: t('name'), flex: 1, valueGetter: ({data}) => ({Name: data.Name, pending: data.pending}),
                    cellRenderer: ({value}) => (
                        <span>
                            <MDBIcon icon="user" color="primary" className="me-1"/>
                            {value.pending && <MDBIcon icon="hourglass" color="primary" className="me-1"/>}
                            {value.Name || t('pending_invitation')}
                        </span>
                    ),
                    sortable: true,
                    comparator: (row1, row2) => DataTableStringComparison(row1.data.Name, row2.data.Name)
                },
                { headerName: t('email'), field: 'mail', flex: 1 },
                { headerName: t('permissions'), valueGetter: ({data}) => data.rights?.Name ?? '', flex: 1 }
            ]}
        />
    ), [users]);

    const UserEditScreen = useMemo(() => typeof editUserId !== 'number' ? null : (
        <UserEdit
            userId={editUserId}
            companyId={userData.company.id}
            canEditPermissions={canEditPermissions}
            canDeleteUser={canDeleteUser}
            self={userData.id === editUserId}
            onClose={() => setEditUserId(null)}
            onEdit={() => fetchUsers()}
            onUserUpdate={onUserUpdate}
        />
    ), [editUserId, userData, canEditPermissions, canDeleteUser, onUserUpdate]);

    const fetchUsers = useCallback(async () => {
        try {
            setUsers(await API.GET('/api/users/u'));
        }catch(err){
        }
    }, []);

    useEffect(() => {
        fetchUsers();
    }, []);

    return (
        <AdminPage
            title={t('users')}
            header={!canAddUser ? null : (
                <MDBBtn color="link" onClick={() => setInviteUser(true)}>
                    <MDBIcon icon="plus-circle" className="me-1"/>
                    {t('invite')}
                </MDBBtn>
            )}
        >
            <UserInviteModal
                open={inviteUser}
                onClose={() => setInviteUser(false)}
                onDone={() => fetchUsers()}
            />
            <SplitScreenDiv
                ratio={60}
                left={Table}
                right={UserEditScreen}
            />
        </AdminPage>
    )
}